import {
	createEntityId,
	EntityTypes,
	idFromEntityId,
	lowerFirstObjectPropertiesNames,
	readURLSearchParams,
	setOtherZone,
} from 'helpers/common';
import { ICropImageData, ICropImageObject, INodeDto, IPage } from 'models/common';
import * as c from 'models/constants';
import { IAppointmentState } from 'models/shop/appointments';
import { ICustomer } from 'models/shop/customer';
import { Effect, IOfficeMember, IRule, IShopAdmin, IShopAdminRule, PolicyType } from 'models/shop/members';
import { IOffice, ITimezoneInfo } from 'models/shop/office';
import { ICreateShopProfile, IShopProfile } from 'models/shop/shopprofile';
import { IUserCard } from 'models/user/usercard';
import moment = require('moment');
import { Moment } from 'moment';
import { IStore } from 'redux/IStore';
import { IServicesList } from 'models/shop/shopBookingTemplates';
import { requestHelper } from 'redux/helpers';
import { IanaToWindowsTimezone } from './timezones/IanaToWindowsTimezone';
import { IFetchDefault } from '../fetch';
import { WindowsToIanaTimezone } from './timezones/WindowsToIanaTimezone';
import { IUserPreferences } from 'models/user/userpreferences';
const config = require('../../../../../config/main');

/****************** HELPERS *********************/

const handleResponse = (response: Response): Response => {
	if (response.ok) {
		return response;
	}
	throw new Error(response.statusText);
};

export const lastMonday = (m: Moment) => m.clone().isoWeekday(1).toISOString(); // last Monday (TODO: test on different days)

export const normalizeDate = (
	date: string | Date,
	mode: IAppointmentState['mode'] = 'week',
	timezone: string,
): string => {
	if (mode === 'day') {
		return momentWithTimezone(timezone, date).toISOString();
	}
	return lastMonday(momentWithTimezone(timezone, date));
};

export const momentWithTimezone = (timezone?: string, date?: moment.MomentInput) => {
	if (timezone in WindowsToIanaTimezone) {
		timezone = WindowsToIanaTimezone[timezone];
	}
	if (date) {
		return timezone ? moment.tz(date, timezone) : moment(date);
	} else {
		return timezone ? moment.tz(timezone) : moment();
	}
};

/**
 * Use this if you want to continue the sequence even if a promise rejected
 */
export async function sequential<T = any>(promises: Array<() => Promise<T>>): Promise<T[]> {
	// eslint-disable-next-line no-async-promise-executor
	return new Promise(async (resolve, reject) => {
		const res: T[] = [];
		for (const p of promises) {
			await p()
				.then((r) => {
					res.push(r);
				})
				.catch(reject);
		}
		resolve(res);
	});
}

/**
 * Use this if you want to catch failed promises and stop the sequence after first reject
 */
export async function sequentialWithBreak<T = any>(promises: Array<() => Promise<T>>): Promise<T[]> {
	// eslint-disable-next-line no-async-promise-executor
	return new Promise(async (resolve, reject) => {
		const res: T[] = [];
		for (const p of promises) {
			try {
				await p().then((r) => {
					res.push(r);
				});
			} catch (e) {
				return reject(e);
			}
		}
		resolve(res);
	});
}

/****************** GET *********************/

export function getShopOffices(
	getState: () => IStore,
	shopId: string,
	fetchAllStaff?: boolean,
	abortController?: AbortController,
): Promise<IOffice[]> {
	return fetch(getState().fetchDefault.baseAPI + 'api/Shop/' + shopId + '/offices', {
		...getState().fetchDefault.requestInit,
		signal: abortController ? abortController.signal : undefined,
	}).then((res) => {
		if (res.ok) {
			return res.json().then((offices: IOffice[]) => {
				if (fetchAllStaff) {
					console.warn('Fetching staff for all offices is expensive');
					return Promise.all(
						offices.map((office) =>
							getOfficeMembers(getState, shopId, office.id, abortController)
								.then((staff) => ({
									...office,
									staff,
								}))
								.catch((res: Response) => {
									throw new Error(res.statusText);
								}),
						),
					);
				} else {
					return offices;
				}
			});
		} else {
			if (res.statusText) {
				throw new Error(res.statusText);
			}
		}
	});
}

export function getOfficeMembers(
	getState: () => IStore,
	shopId: string,
	officeId: string,
	abortController?: AbortController,
) {
	const isSiteAdmin: boolean = getState().userPreferences.isAdministrator;
	const isShopAdmin: boolean = isAdmin(shopId, getState().userPreferences.userRoles);

	if (isShopAdmin || isSiteAdmin) {
		// here we check if the current user is Admin or owner of the selected shop, because only Admin and owner can call api/permission/getByObject
		// first we check if role is defined, then we check if the user is Admin, since only Admin gets EntityId with type Shop and therefore shop's id
		// owner doesn't get Entity id with type Shop for some reason, so we check if the current user's role is Admin and if it has stored
		// the selected shop's id in role[selectedShopIndex].attributes.ShopId
		return requestHelper<IOfficeMember[]>(
			fetch(getState().fetchDefault.baseAPI + 'api/Shop/' + officeId + '/Resources', {
				...getState().fetchDefault.requestInit,
				signal: abortController ? abortController.signal : undefined,
			}),
		).then((members) =>
			Promise.all(
				members.map((member) =>
					requestHelper<IRule[]>(
						fetch(
							getState().fetchDefault.baseAPI +
								'api/permission/getByObject?type=Office&id=' +
								idFromEntityId(member?.officeId, false),
							Object.assign({}, getState().fetchDefault.requestInit),
						),
					).then((rules) => ({
						...member,
						rules: rules.filter((rule) => rule.sub === member.userId),
					})),
				),
			),
		);
	}
	return requestHelper<IOfficeMember[]>(
		fetch(getState().fetchDefault.baseAPI + 'api/Shop/' + officeId + '/Resources', {
			...getState().fetchDefault.requestInit,
			signal: abortController ? abortController.signal : undefined,
		}),
	);
}

export function getCustomers(
	getState: () => IStore,
	shopId: string,
	currentPage: number,
	keyword?: string,
): Promise<IPage<ICustomer>> {
	const searchQuery = new URLSearchParams();
	searchQuery.set('currentPage', String(currentPage));
	searchQuery.set('itemsPerPage', String(c.SHOPCARD_TAB_CUSTOMERS_ITEMSPERPAGE));

	if (keyword) {
		searchQuery.set('keyword', keyword);
	}

	return requestHelper(
		fetch(
			getState().fetchDefault.baseAPI + 'api/Shop/' + shopId + `/Customers?${searchQuery}`,
			Object.assign({}, getState().fetchDefault.requestInit),
		),
	);
}

export function getShopAdmins(
	getState: () => IStore,
	shopId: string,
	abortController?: AbortController,
	fetchUserData?: true,
): Promise<IShopAdmin[]>;
export function getShopAdmins(
	getState: () => IStore,
	shopId: string,
	abortController?: AbortController,
	fetchUserData?: false,
): Promise<IShopAdminRule[]>;
export function getShopAdmins(
	getState: () => IStore,
	shopId: string,
	abortController?: AbortController,
	fetchUserData: boolean = true,
): Promise<IShopAdmin[] | IShopAdminRule[]> {
	return (
		requestHelper<IRule[]>(
			fetch(
				getState().fetchDefault.baseAPI + 'api/permission/getByObject?type=Shop&id=' + idFromEntityId(shopId, false),
				{
					...getState().fetchDefault.requestInit,
					signal: abortController ? abortController.signal : undefined,
				},
			),
		)
			// get users for each IRule where user has CRUD rights
			.then((rules) =>
				Promise.all(
					rules
						.filter(
							(rule) =>
								rule.actions.toLowerCase().indexOf('all') > -1 ||
								(rule.actions.toLowerCase().indexOf('create') > -1 &&
									rule.actions.toLowerCase().indexOf('read') > -1 &&
									rule.actions.toLowerCase().indexOf('update') > -1 &&
									rule.actions.toLowerCase().indexOf('delete') > -1),
						)
						.map((rule) =>
							fetchUserData
								? requestHelper<IUserCard>(
										fetch(
											getState().fetchDefault.baseAPI + 'api/user/' + idFromEntityId(rule.sub, false),
											Object.assign({}, getState().fetchDefault.requestInit),
										),
								  ).then(
										(user) =>
											({
												...user,
												rule,
											}) as IShopAdmin,
								  )
								: ({ rule, id: rule.sub } as any),
						),
				),
			)
			.catch(() => new Promise((res) => res([])))
	);
}

export function getOfficeBookingTemplates(
	getState: () => IStore,
	officeId: string,
	loadResources?: boolean,
	abortController?: AbortController,
): Promise<IServicesList[]> {
	return requestHelper<IServicesList[] | IServicesList>(
		fetch(
			getState().fetchDefault.baseAPI +
				'api/Shop/' +
				officeId +
				'/GetBookingTemplatesByOffice' +
				`?loadResources=${loadResources ? true : false}`,
			{
				...getState().fetchDefault.requestInit,
				signal: abortController ? abortController.signal : undefined,
			},
		),
	)
		.then((res) => (Array.isArray(res) ? res : [res]))
		.then((res) => res.reverse()) // API returns last created results first
		.then((templates) => {
			return templates;
		});
}

export function getServiceGroupBookingTemplates(
	getState: () => IStore,
	serviceGroupId: string,
	loadResources?: boolean,
): Promise<IServicesList[]> {
	return requestHelper<IServicesList[] | IServicesList>(
		fetch(
			getState().fetchDefault.baseAPI +
				'api/Shop/' +
				serviceGroupId +
				'/GetBookingTemplatesByServiceGroup' +
				`?loadResources=${loadResources ? true : false}`,
			{ ...getState().fetchDefault.requestInit },
		),
	)
		.then((res) => (Array.isArray(res) ? res : [res]))
		.then((templates) => {
			return templates;
		});
}

export function fetchBookingTemplateVariants(
	getState: () => IStore,
	varientParentId: string,
	loadResources?: boolean,
	loadChildrenBookingTemplates?: boolean,
) {
	return requestHelper<IServicesList[]>(
		fetch(
			getState().fetchDefault.baseAPI + 'api/Shop/BookingTemplateVariants/' + varientParentId,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'GET',
			}),
		),
	).then((res: IServicesList[]) => {
		if (loadResources || loadChildrenBookingTemplates) {
			return Promise.all(
				res.map((bt) => getBookingTemplateById(getState, bt.id, loadResources, loadChildrenBookingTemplates)),
			);
		} else {
			return res;
		}
	});
}

export function deleteBookingTemplateVariants(getState: () => IStore, varientParentId: string) {
	return requestHelper<IServicesList[]>(
		fetch(
			getState().fetchDefault.baseAPI + 'api/Shop/BookingTemplateVariants/?variantParentId=' + varientParentId,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'DELETE',
			}),
		),
	).then((res: IServicesList[]) => {
		return res;
	});
}

export function getBookingTemplateById(
	getState: () => IStore,
	templateId: string,
	loadResources?: boolean,
	loadChildrenBookingTemplates?: boolean,
	abortController?: AbortController,
): Promise<IServicesList> {
	const querys: string[] = [];
	if (loadResources) {
		querys.push('loadResources=true');
	}
	if (loadChildrenBookingTemplates) {
		querys.push('loadChildrenBookingTemplates=true');
	}
	return requestHelper<IServicesList>(
		fetch(
			getState().fetchDefault.baseAPI +
				'api/Shop/BookingTemplates/' +
				templateId +
				(querys.length ? '?' + querys.join('&') : ''),
			{
				...getState().fetchDefault.requestInit,
				signal: abortController ? abortController.signal : undefined,
			},
		),
	);
}

export function getBundlesForChildren(getState: () => IStore, childrenBookingTemplateIds: string[]): Promise<any> {
	if (!childrenBookingTemplateIds) {
		return Promise.reject('No children provided!');
	}

	return requestHelper(
		fetch(
			getState().fetchDefault.baseAPI + 'api/Shop/GetBundlesForChildren/',
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'POST',
				body: JSON.stringify(childrenBookingTemplateIds),
			}),
		),
	);
}

/****************** DIFFERENCES *********************/

interface IShopDifference<TItem> {
	item: TItem;
	action: 'delete' | 'create' | 'update';
}

export function getOfficesDifferences(
	previous: IOffice[],
	next: IOffice[],
	shopId: IShopProfile['id'],
): Array<IShopDifference<IOffice>> {
	next = next.map((n) => {
		if (!n || n.officeTimes) {
			return n;
		}
		return {
			...n,
			officeTimes: [],
		};
	});
	const nextIds = next.map((n) => n.id).filter(Boolean);
	const toDelete: IOffice[] = previous.filter((p) => !nextIds.includes(p.id));
	const toCreate: IOffice[] = next.filter((n) => !n.id);
	const toUpdate: IOffice[] = [];

	for (const office of next) {
		if (office.shopId && office.shopId !== shopId) {
			console.error('Office shopId mismatch');
			throw new Error('Office shopId mismatch');
		}
	}

	for (const n of next) {
		if (!n.id) {
			continue;
		}
		const prev = previous.find((p) => p.id === n.id);
		if (!prev) {
			console.error('Cannot find previous state of office ID ' + n.id);
			continue;
		}
		toUpdate.push(n);
	}

	return [
		...toDelete.map((office) => ({ item: { ...office, shopId }, action: 'delete' as const })),
		...toCreate.map((office) => ({ item: { ...office, shopId }, action: 'create' as const })),
		...toUpdate.map((office) => ({ item: { ...office, shopId }, action: 'update' as const })),
	];
}

export function getMembersDifferences<THolder extends IShopProfile | IOffice>(
	previousOfficeOrShop: THolder,
	nextOfficeOrShop: THolder,
): Array<IShopDifference<IUserCard | INodeDto>> {
	const getUserId = (item: IUserCard | INodeDto): string => {
		if ('id' in item) {
			return item.id;
		}
		if ('entityId' in item) {
			return idFromEntityId(item.entityId, false);
		}
	};
	const isOrderSame = (item: IOfficeMember): boolean => {
		const prevMember: any = previous?.find((mem) => mem.id === item.id) || null;

		if (!prevMember) {
			return false;
		} else {
			if (item?.order !== prevMember?.order) {
				return true;
			}
			return false;
		}
	};

	let type: 'staff' | 'admins' = 'admins';
	if ('staff' in previousOfficeOrShop || 'staff' in nextOfficeOrShop) {
		type = 'staff';
	}
	const previous = (previousOfficeOrShop as IShopProfile)[type as 'admins'] || [];
	const next = (nextOfficeOrShop as IOffice)[type as 'staff'] || [];
	const nextIds = next.map((n) => getUserId(n)).filter(Boolean);
	const previousIds = previous.map((n) => getUserId(n)).filter(Boolean);
	const toDelete: Array<IUserCard | INodeDto> = previous.filter((p) => !nextIds.includes(getUserId(p)));
	const toCreate: Array<IUserCard | INodeDto> = next.filter((n) => !previousIds.includes(getUserId(n)));
	const toUpdate: Array<IUserCard | INodeDto> = next.filter((n: IOfficeMember) => isOrderSame(n));

	return [
		...toDelete.map((item) => ({ item, action: 'delete' as const })),
		...toCreate.map((item) => ({ item, action: 'create' as const })),
		...toUpdate.map((item) => ({ item, action: 'update' as const })),
	];
}

/****************** PUT/POST/DELETE *********************/

/**** OFFICE ****/

// get office timezone from google api
export const getOfficeTimezone = (office: IOffice): Promise<ITimezoneInfo> => {
	const url = `https://maps.googleapis.com/maps/api/timezone/json?location=${office.geoLocationLatitude}%2C${
		office.geoLocationLongitude
	}&timestamp=${Date.now() / 1000}&key=${config.app.googleSettings.mapsApi}`;

	return fetch(url, {
		method: 'GET',
		credentials: 'omit',
		headers: {
			Accept: 'application/json',
		},
	})
		.then((res) => res.json())
		.then((res) => res);
};

export const getOfficeById = async (fetchDefault: IFetchDefault, officeId: string): Promise<IOffice> =>
	requestHelper<IOffice>(
		fetch(
			fetchDefault.baseAPI + 'api/shop/office/' + officeId,
			Object.assign({}, fetchDefault.requestInit, {
				method: 'GET',
			}),
		).then(handleResponse),
	);

export const addOffices = (getState: () => IStore, copy: ICreateShopProfile, shop: IShopProfile) =>
	Promise.all(
		copy.offices.map((createOffice, index) => {
			const office: IOffice = {
				shopId: shop.id,
				name: createOffice.name,
				email: createOffice.email,
				callingCodeKey: createOffice.callingCodeKey,
				phoneNumber: createOffice.phoneNumber,
				location: createOffice.location,
				countryKey: createOffice.countryKey,
				area: createOffice.area,
				geoLocationLatitude: createOffice.geoLocationLatitude,
				geoLocationLongitude: createOffice.geoLocationLongitude,
				officeTimes: (createOffice.officeTimes || []).map(lowerFirstObjectPropertiesNames).map((time) => ({
					day: time.day,
					openTime: time.openTime,
					closeTime: time.closeTime,
				})),
				isDefault: createOffice.isDefault,
			};

			return getOfficeTimezone(office).then((res) => {
				const zoneInfo = IanaToWindowsTimezone[res.timeZoneId]; // Convert IANA timezone to Windows timezone
				const officeWithTimezone: IOffice = { ...office, zoneInfo: zoneInfo };
				if (officeWithTimezone.officeTimes) {
					officeWithTimezone.officeTimes.map((officeTime) => {
						var newOpenTime = setOtherZone(new Date(officeTime.openTime), res.timeZoneId);
						var newCloseTime = setOtherZone(new Date(officeTime.closeTime), res.timeZoneId);
						officeTime.openTime = newOpenTime;
						officeTime.closeTime = newCloseTime;
					});
				}
				addOffice(getState, officeWithTimezone).then(addMembers(getState, copy, index));
			});
		}),
	).then(() => shop);

export const addOffice = (getState: () => IStore, office: IOffice): Promise<IOffice['id']> =>
	fetch(
		getState().fetchDefault.baseAPI + 'api/shop/offices',
		Object.assign({}, getState().fetchDefault.requestInit, {
			method: 'POST',
			body: JSON.stringify(office),
		}),
	).then((res) => {
		if (res.ok) {
			const locationParts = res.headers.get('Location')?.split('/').filter(Boolean);
			if (locationParts?.length) {
				return locationParts.pop();
			}
			throw new Error('Cannot get office ID');
		}
		throw new Error(res.statusText);
	});

export const setDefaultOffice = (getState: () => IStore, shop: IShopProfile): Promise<Response | true> => {
	const offices = shop?.offices || [];
	if (!offices.length) {
		return Promise.resolve(true);
	}
	const defaultOffice = (shop.offices || []).find((o) => o.isDefault);
	const defaultId = defaultOffice ? defaultOffice.id : offices[0].id;
	if (!defaultId) {
		return Promise.resolve(true);
	}
	return fetch(
		getState().fetchDefault.baseAPI + 'api/shop/office/' + defaultId + '/setDefault',
		Object.assign({}, getState().fetchDefault.requestInit, {
			method: 'PUT',
		}),
	).then(handleResponse);
};

export const removeOffice = (getState: () => IStore, officeId: IOffice['id']): Promise<Response> =>
	fetch(
		getState().fetchDefault.baseAPI + 'api/shop/offices/' + officeId,
		Object.assign({}, getState().fetchDefault.requestInit, {
			method: 'DELETE',
		}),
	).then(handleResponse);

export const updateOffice = (getState: () => IStore, office: IOffice): Promise<Response> =>
	fetch(
		getState().fetchDefault.baseAPI + 'api/shop/offices/' + office.id,
		Object.assign({}, getState().fetchDefault.requestInit, {
			method: 'PUT',
			body: JSON.stringify(office),
		}),
	).then(handleResponse);

/**** MEMBER (STAFF) ****/

export const addMembers =
	(getState: () => IStore, copy: ICreateShopProfile, officeIndex: number) => (officeId: IOffice['id']) => {
		// When creating a shop don't add member permissions to shop owner or admins
		const arrayAdmins = [];
		arrayAdmins.push(copy.ownerId);
		for (let i = 0; i < copy.admins?.length; i = i + 1) {
			arrayAdmins.push(JSON.parse(copy.admins[i].entityId).id);
		}

		return Promise.all(
			(copy.offices[officeIndex].staff || []).map((member) => {
				const shouldRemovePermission = !arrayAdmins.includes(member.userId);
				addMember(getState, member, officeId, shouldRemovePermission);
			}),
		).then(() => officeId);
	};

export const addMember = (
	getState: () => IStore,
	member: INodeDto | IUserCard | IOfficeMember,
	officeId: IOffice['id'],
	shouldAddPermission?: boolean,
) =>
	fetch(
		getState().fetchDefault.baseAPI + `api/shop/Resources`,
		Object.assign({}, getState().fetchDefault.requestInit, {
			method: 'POST',
			body: JSON.stringify(
				Object.assign({}, member, {
					officeId: idFromEntityId(officeId, false),
				}),
			),
		}),
	) && shouldAddPermission
		? addMemberPermission(getState, officeId, member?.userId, 'Read')
		: null;
export const removeMember = (
	getState: () => IStore,
	member: INodeDto | IOfficeMember,
	shouldRemovePermission?: boolean,
) =>
	fetch(
		getState().fetchDefault.baseAPI + 'api/shop/Resources/' + member.id,
		Object.assign({}, getState().fetchDefault.requestInit, {
			method: 'DELETE',
		}),
	) && shouldRemovePermission
		? removeMemberPermission(getState, member).then(handleResponse)
		: null;
export const editMember = (getState: () => IStore, member: INodeDto | IOfficeMember) =>
	fetch(
		getState().fetchDefault.baseAPI + 'api/shop/Resources/' + member.id,
		Object.assign({}, getState().fetchDefault.requestInit, {
			method: 'PUT',
			body: JSON.stringify(member),
		}),
	).then(handleResponse);

/**** ADMINS ****/

const addPermission = (getState: () => IStore, shopId: IShopProfile['id'], userId: string, actions: string) =>
	fetch(
		getState().fetchDefault.baseAPI + 'api/permission',
		Object.assign({}, getState().fetchDefault.requestInit, {
			method: 'POST',
			body: JSON.stringify({
				sub: idFromEntityId(userId), // user
				obj: createEntityId(shopId, EntityTypes.Shop, true), // shop
				actions,
				effect: Effect.Allow,
				policyType: PolicyType.User,
			} as IRule),
		}),
	).then(handleResponse);
export const addMemberPermission = (
	getState: () => IStore,
	officeId: IOffice['id'],
	memberId: INodeDto['id'],
	actions: string,
) =>
	fetch(
		getState().fetchDefault.baseAPI + 'api/permission/',
		Object.assign({}, getState().fetchDefault.requestInit, {
			method: 'POST',
			body: JSON.stringify({
				sub: idFromEntityId(memberId), // user
				obj: createEntityId(officeId, EntityTypes.Office, true),
				actions,
				effect: Effect.Allow,
				policyType: PolicyType.User,
			} as IRule),
		}),
	);

export const removeMemberPermission = (getState: () => IStore, member: IOfficeMember | INodeDto) =>
	'rules' in member ? removePermission(getState, member.rules.pop()) : Promise.reject('No rule in member');

export const removePermission = (getState: () => IStore, permission: IRule) =>
	fetch(
		getState().fetchDefault.baseAPI + 'api/permission/' + permission.id,
		Object.assign({}, getState().fetchDefault.requestInit, {
			method: 'DELETE',
		}),
	).then(handleResponse);

export const addAdmins = (getState: () => IStore, copy: ICreateShopProfile, shop: IShopProfile) =>
	Promise.all((copy.admins || []).map((admin) => addAdmin(getState, shop.id, admin))).then(() => shop);

export const addAdmin = (getState: () => IStore, shopId: IShopProfile['id'], user: IUserCard | INodeDto) =>
	addPermission(getState, shopId, user.entityId || (user as IShopProfile).id, 'Create,Read,Update,Delete');

export const removeAdmin = (getState: () => IStore, user: IShopAdmin | IUserCard | INodeDto) => {
	if (!('rule' in user)) {
		console.error('Cannot remove admin without IRule');
		return Promise.resolve(false);
	}
	return removePermission(getState, user.rule);
};

/**** IMAGES ****/

export const addImage = (
	getState: () => IStore,
	shopId: IShopProfile['id'],
	imageId: string | undefined,
	imageUrl: string | undefined,
	cropObject: ICropImageObject,
): Promise<false | Response> => {
	if (!imageUrl || !imageId || !cropObject) {
		return Promise.resolve(false);
	}
	const body: ICropImageData = {
		entityReferenceId: shopId,
		entityType: c.NODE_TYPE_SHOP,
		...cropObject,
		id: imageId,
		url: imageUrl,
	};
	return fetch(
		getState().fetchDefault.baseAPI + 'api/shopProfile/profileImage',
		Object.assign({}, getState().fetchDefault.requestInit, {
			method: 'PUT',
			body: JSON.stringify(body),
		}),
	).then(handleResponse);
};

export const addImageToShop = (getState: () => IStore, copy: ICreateShopProfile, shop: IShopProfile) =>
	addImage(getState, shop.id, copy.imageId, copy.imageUrl, copy.imageCrop).then(() => shop);
/**
 * is admin of shop or office?
 * @param id id of the shop or office
 * @param userRoles userPreferences.userRoles
 */
export const isAdmin = (id, userRoles): boolean =>
	!!userRoles?.find((role) => role.objId === id && role.role === 'Admin');

export const isPlatformAdmin = (userPreferences: IUserPreferences): boolean => userPreferences?.isAdministrator;

export const isRestrictedModeQuery = (location): boolean =>
	location?.search && !!readURLSearchParams(c.RESTRICTED_MODE);

export function getActiveShopSubscriptionPlan(getState: () => IStore, shopId: string): Promise<any> {
	if (!shopId) {
		return Promise.resolve(false);
	}

	return requestHelper(
		fetch(
			getState().fetchDefault.baseAPI + `api/Shop/${shopId}/SubscriptionPlan`,
			Object.assign({}, getState().fetchDefault.requestInit, {
				method: 'GET',
			}),
		),
	);
}
